import { zodResolver } from "@hookform/resolvers/zod";
import { format, isValid, parseISO } from "date-fns";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { TranslationFunction } from "../../../i18n";
import {
  ScheduleConfiguration,
  ScheduleFrequency,
  ScheduleSettings,
} from "./types";
import { dayOfMonthOptions } from "./utils";

const ScheduleConfigSchema = z.object({
  frequency: z.enum([
    "daily",
    "weekly",
    "monthly",
    "yearly",
    "Daily",
    "Weekly",
    "Monthly",
    "Yearly",
  ]),
  dayOfWeek: z.string().optional(),
  dayOfMonth: z.string().optional(),
  yearlyDate: z.date().optional(),
  hour: z.string().min(1),
  minute: z.string().min(1),
});

export type ScheduleFormData = z.infer<typeof ScheduleConfigSchema>;

const parseDate = (dateValue: Date | string | undefined): Date | undefined => {
  if (!dateValue) return undefined;
  if (dateValue instanceof Date)
    return isValid(dateValue) ? dateValue : undefined;

  const parsed = parseISO(dateValue);
  return isValid(parsed) ? parsed : undefined;
};

const normalizeFrequency = (frequency: string): ScheduleFrequency => {
  const normalized = frequency.toLowerCase();
  if (["daily", "weekly", "monthly", "yearly"].includes(normalized)) {
    return normalized as ScheduleFrequency;
  }
  return "daily";
};

export const useScheduleForm = (config: ScheduleConfiguration) => {
  const form = useForm<ScheduleFormData>({
    resolver: zodResolver(ScheduleConfigSchema),
    defaultValues: {
      frequency: normalizeFrequency(config.settings.frequency),
      dayOfWeek: config.settings.dayOfWeek,
      dayOfMonth: config.settings.dayOfMonth,
      yearlyDate: parseDate(config.settings.yearlyDate),
      hour: config.settings.hour?.toString().padStart(2, "0"),
      minute: config.settings.minute?.toString().padStart(2, "0"),
    },
    mode: "onTouched",
  });

  useEffect(() => {
    form.reset({
      frequency: normalizeFrequency(config.settings.frequency),
      dayOfWeek: config.settings.dayOfWeek,
      dayOfMonth: config.settings.dayOfMonth,
      yearlyDate: parseDate(config.settings.yearlyDate),
      hour: config.settings.hour?.toString().padStart(2, "0"),
      minute: config.settings.minute?.toString().padStart(2, "0"),
    });
  }, [config.settings, form]);

  return form;
};

export const useScheduleConfig = (
  config: ScheduleConfiguration,
  onConfigUpdate: (config: ScheduleConfiguration) => void,
  t: TranslationFunction,
) => {
  const form = useScheduleForm(config);

  const updateSettings = (updates: Partial<ScheduleSettings>) => {
    const currentValues = form.getValues();
    const newSettings = {
      ...config.settings,
      ...currentValues,
      ...updates,
      frequency: updates.frequency
        ? normalizeFrequency(updates.frequency)
        : normalizeFrequency(
            currentValues.frequency || config.settings.frequency || "daily",
          ),
    };
    const cronPattern = generateCronPattern(newSettings);
    const description = generateDescription(newSettings, t);

    const newConfig: ScheduleConfiguration = {
      cronPattern,
      description,
      frequency: newSettings.frequency,
      settings: newSettings,
    };
    onConfigUpdate(newConfig);
  };

  const onFormSubmit = (values: ScheduleFormData) => {
    const newSettings: ScheduleSettings = {
      frequency: normalizeFrequency(values.frequency),
      dayOfWeek: values.dayOfWeek,
      dayOfMonth: values.dayOfMonth,
      yearlyDate: values.yearlyDate,
      hour: values.hour,
      minute: values.minute,
    };

    const cronPattern = generateCronPattern(newSettings);
    const description = generateDescription(newSettings, t);

    const newConfig: ScheduleConfiguration = {
      cronPattern,
      description,
      frequency: newSettings.frequency,
      settings: newSettings,
    };
    onConfigUpdate(newConfig);
  };

  return {
    form,
    updateSettings,
    onFormSubmit,
  };
};

export const generateCronPattern = (settings: ScheduleSettings): string => {
  const { frequency, dayOfWeek, dayOfMonth, yearlyDate, hour, minute } =
    settings;

  switch (frequency) {
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekly":
      return `${minute} ${hour} * * ${dayOfWeek}`;
    case "monthly":
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case "yearly": {
      const validDate = parseDate(yearlyDate);
      if (validDate) {
        const day = validDate.getDate();
        const month = validDate.getMonth() + 1;
        return `${minute} ${hour} ${day} ${month} *`;
      }
      break;
    }
    default:
      break;
  }
  return `${minute} ${hour} * * *`;
};

export const generateDescription = (
  settings: ScheduleSettings,
  t: TranslationFunction,
): string => {
  const { frequency, dayOfWeek, dayOfMonth, yearlyDate, hour, minute } =
    settings;

  const timeStr = `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;

  switch (frequency) {
    case "daily":
      return t("components.scheduleControl.descriptions.daily", timeStr);
    case "weekly": {
      const weekdayKeys = [
        "components.scheduleControl.weekday.sunday",
        "components.scheduleControl.weekday.monday",
        "components.scheduleControl.weekday.tuesday",
        "components.scheduleControl.weekday.wednesday",
        "components.scheduleControl.weekday.thursday",
        "components.scheduleControl.weekday.friday",
        "components.scheduleControl.weekday.saturday",
      ];
      const index = Number(dayOfWeek ?? "1");
      const dayNameKey =
        weekdayKeys[index] || "components.scheduleControl.weekday.monday";
      const dayName = t(dayNameKey);
      return t("components.scheduleControl.descriptions.weekly", {
        dayName,
        time: timeStr,
      });
    }
    case "monthly": {
      const dayStr =
        dayOfMonthOptions.find((d) => d.id === dayOfMonth)?.text || "1st";
      return t("components.scheduleControl.descriptions.monthly", {
        dayNumber: dayStr,
        time: timeStr,
      });
    }
    case "yearly": {
      const validDate = parseDate(yearlyDate);
      if (validDate) {
        const dateStr = format(validDate, "MMMM do");
        return t("components.scheduleControl.descriptions.yearly", {
          date: dateStr,
          time: timeStr,
        });
      }
      return t("components.scheduleControl.descriptions.yearlyNoDate");
    }
    default:
      return t("components.scheduleControl.descriptions.daily", timeStr);
  }
};
